import {
  Box,
  Flex,
  Grid,
  Radio,
  RadioGroup,
  Text,
  Tooltip
} from "@chakra-ui/react";
import { DropdownField } from "components/dropdown-field";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import api from "api/api";
import { UsuarioBanco } from "components/usuarios-bancos-components/page";
import { Toast } from "components/toast";
import { initialDigitacaoPipelineFilterValues } from "../../variables";
import { MailingData, MailingOptionFilter } from "../..";
import apiDigitacao from "api/api-digitacao";
import { useApplicationContext } from "contexts/ApplicationContext";
import { isUserGerente } from "utils/is-gerente";
import {
  CustomTableColor,
  CustomTableColorColumn
} from "components/dynamic-table/color-table";
import { Checkbox } from "components/checkbox";
import { InfoIcon } from "@chakra-ui/icons";
import { NovaIsencaoField } from "./nova-isencao";

interface TabelaDigitacaoProps {
  banco: string;
  codigo: string;
  convenio: string;
  descricao: string;
  prazo: null;
}

interface PacotesBeneficiosRowProps {
  code: string;
  description: string;
  amount: number;
  exempt: string;
  type_code: number;
  item_number: number;
  group_code: string;
  name?: string;
}

export function DigitacaoFilter({
  set,
  mailingData,
  currentDigitacao,
  invalidFields,
  setInvalidFields,
  enabledPacotesBeneficios,
  setEnabledPacotesBeneficios
}: {
  currentDigitacao: MailingOptionFilter;
  mailingData: Partial<MailingData>;
  set: Dispatch<SetStateAction<MailingOptionFilter>>;
  invalidFields: { [field: string]: string };
  setInvalidFields: Dispatch<SetStateAction<{ [field: string]: string }>>;
  enabledPacotesBeneficios: boolean;
  setEnabledPacotesBeneficios: Dispatch<SetStateAction<boolean>>;
}) {
  const [loading, setLoading] = useState(false);
  const [disabledBancos, setDisabledBancos] = useState(false);
  const [usuariosBancos, setUsuariosBancos] = useState<UsuarioBanco[]>([]);
  const [tabelas, setTabelas] = useState<TabelaDigitacaoProps[]>([]);
  const [pacotesBeneficios, setPacotesBeneficios] = useState<
    PacotesBeneficiosRowProps[]
  >([]);
  const [loadingPacotesBeneficios, setLoadingPacotesBeneficios] =
    useState(false);
  const [loadingTabelas, setLoadingTabelas] = useState(false);
  const { user } = useApplicationContext();
  const isGerente = isUserGerente(user);

  if (!currentDigitacao.usuarioDigitacaoSenha) {
    currentDigitacao.usuarioDigitacaoSenha =
      initialDigitacaoPipelineFilterValues.usuarioDigitacaoSenha;
  }
  const tipoOperacao = mailingData.tipoOperacao;
  const bancoOptions = ["REFIN_CARTEIRA"].includes(tipoOperacao!)
    ? bancosDigitacaoMailingRefinCarteira
    : bancosDigitacaoMailingPipeline;

  const tabelasOptions = tabelas.map((crrTabela) => ({
    name: crrTabela.descricao,
    value: crrTabela.codigo
  }));

  const banco = currentDigitacao.banco.value;
  const tipoColunaMargem = currentDigitacao.tipoColunaMargem.value;
  const usuarioDigitacao = currentDigitacao.usuarioDigitacao.value;
  const tabela = currentDigitacao.tabela.value;
  const convenio = mailingData.convenio;
  const typeCodeC6 = currentDigitacao.typeCodeC6.value;
  const novaIsencao = currentDigitacao.novaIsencao?.value;

  const usuarioDataDigitacao = new Map<string, UsuarioBanco>();

  const currTabelaDesc = tabelasOptions.find(
    (crr) => crr.value === tabela
  )?.name;

  const usuariosDigitacaoOptions = usuariosBancos.map((user) => {
    usuarioDataDigitacao.set(user.username, user);
    return { name: user.username, value: user.username };
  });
  const selectedUser = usuarioDataDigitacao.get(usuarioDigitacao);
  const selectedTipoColunaMargem = tipoColunaMargemOptions.find(
    (crr) => crr.value === tipoColunaMargem
  );

  const getPacotesBeneficios = async () => {
    setLoadingPacotesBeneficios(true);
    try {
      const { data } = await apiDigitacao.post<{
        expenses: PacotesBeneficiosRowProps[];
      }>(`/api/simulacoes/inss/c6`, {
        banco: banco,
        incomeAmount: 1518,
        productTypeCode: selectedTipoColunaMargem?.productType,
        username: selectedUser?.username,
        password: selectedUser?.password,
        tableCode: tabela
      });
      setPacotesBeneficios(
        data.expenses.map((p, i, arr) => {
          const isLast = i + 1 === arr.length;
          return { ...p, name: `Pacote ${isLast ? "Máximo" : i + 1}` };
        })
      );
    } catch (e: any) {
      const errorMessage = e?.response?.data?.msgInterface;
      Toast({
        title: "Erro ao obter pacotes de benefícios.",
        description: errorMessage
      });
    } finally {
      setLoadingPacotesBeneficios(false);
    }
  };

  const getTabelasDigitacao = async () => {
    setLoadingTabelas(true);
    try {
      const { data } = await apiDigitacao.get<TabelaDigitacaoProps[]>(
        `api/tabelas-digitacao/enable`,
        { params: { convenio, banco, customer_id: mailingData.customerId } }
      );
      if (!data?.length)
        Toast({ title: "Não há tabelas para o banco selecionado" });
      setTabelas(data);
    } catch (e) {
      Toast({ title: "Erro ao obter tabelas" });
    } finally {
      setLoadingTabelas(false);
    }
  };

  const setDigitacao = (
    k:
      | "banco"
      | "usuarioDigitacao"
      | "tabela"
      | "usuarioDigitacaoSenha"
      | "cpfUsuarioCertificado"
      | "tipoColunaMargem"
      | "typeCodeC6"
      | "novaIsencao",

    value: { isChecked?: boolean; value?: any }
  ) => {
    set((prev) => ({ ...prev, [k]: { ...prev[k], ...value } }));

    setInvalidFields((errors) => {
      delete errors[k];
      return { ...errors };
    });
    if (k !== "typeCodeC6") {
      setDigitacao("typeCodeC6", { value: undefined });
      setEnabledPacotesBeneficios(false);
    }
  };

  const getUsuariosBancos = async () => {
    setLoading(true);
    try {
      const { data } = await api.get<UsuarioBanco[]>(
        `usuarios-bancos/digitadores/${banco}`,
        {
          params: {
            customer_id: isGerente ? mailingData.customerId : undefined
          }
        }
      );
      if (!data?.length) {
        Toast({
          title: `Não há usuários cadastrados para o banco selecionado`,
          duration: 5000
        });
      }
      setUsuariosBancos(data);
    } catch (error) {
      Toast({
        title: "Erro ao buscar lista de Usuario Banco",
        status: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (banco) {
      getUsuariosBancos();
      getTabelasDigitacao();
    }
  }, [banco]);

  useEffect(() => {
    if (mailingData.bancosTo?.[0]) {
      if (mailingData.bancosTo?.[0] === 935) {
        setDisabledBancos(true);
        setDigitacao("banco", { value: "FACTA" });
      }
      if (mailingData.bancosTo?.[0] === 626) {
        setDisabledBancos(true);
        setDigitacao("banco", { value: "C6" });
      }
      if (mailingData.bancosTo?.[0] === 318) {
        setDisabledBancos(true);
        setDigitacao("banco", { value: "BMG" });
      }
    }
    if (!currentDigitacao.usuarioDigitacaoSenha)
      setDigitacao(
        "usuarioDigitacaoSenha",
        initialDigitacaoPipelineFilterValues.usuarioDigitacaoSenha
      );
  }, []);

  useEffect(() => {
    setDigitacao("typeCodeC6", {
      isChecked: enabledPacotesBeneficios,
      value: undefined
    });
    if (!enabledPacotesBeneficios) {
      setPacotesBeneficios([]);
    }
  }, [enabledPacotesBeneficios]);

  const colunasPacotesBeneficios: CustomTableColorColumn<PacotesBeneficiosRowProps>[] =
    [
      {
        name: "Despesas / Tarifas",
        render: (row, i) => {
          const isLast = i + 1 === pacotesBeneficios.length;
          return (
            <Flex alignItems="center">
              {row.name}{" "}
              {isLast && (
                <Tooltip
                  label={row.description}
                  bg="white"
                  border="1px solid var(--chakra-colors-custom-gray)"
                  color="prymary.500"
                  boxShadow="none"
                  borderRadius="10px"
                >
                  <InfoIcon display="block" ml="2" color="primary.300" />
                </Tooltip>
              )}
            </Flex>
          );
        }
      },
      {
        name: "Incluir",
        render: (row) => (
          <Flex>
            <Checkbox
              isChecked={typeCodeC6 === row.type_code}
              onChange={() => {
                if (typeCodeC6 === row.type_code) {
                  setDigitacao("typeCodeC6", { value: undefined });
                } else setDigitacao("typeCodeC6", { value: row.type_code });
              }}
              errorMessage={invalidFields["typeCodeC6"]}
            />
          </Flex>
        )
      },
      { name: "Valor", render: (row) => `R$ ${row.amount ?? "MÁX."}` }
    ];
  console.log(tipoColunaMargem);
  return (
    <Box>
      <Grid
        templateColumns={
          tipoOperacao === "REFIN_CARTEIRA" ? "1fr 1fr 1fr" : "1fr 1fr"
        }
        gap="10px"
        mb="10px"
      >
        {tipoOperacao === "REFIN_CARTEIRA" ? null : (
          <Box>
            <DropdownField
              title="Campo para digitação"
              dropdownProps={{ w: "100%" }}
              onChange={(value) => setDigitacao("tipoColunaMargem", { value })}
              value={tipoColunaMargem}
              options={tipoColunaMargemOptions}
              errorMessage={invalidFields["tipoColunaMargem"]}
            />
          </Box>
        )}

        <Box>
          <DropdownField
            title="Banco"
            dropdownProps={{ w: "100%" }}
            onChange={(value) => {
              setDigitacao("banco", { value });
              setDigitacao("usuarioDigitacao", { value: "" });
              setDigitacao("usuarioDigitacaoSenha", { value: "" });
            }}
            value={banco}
            options={bancoOptions}
            isDisabled={disabledBancos}
            errorMessage={invalidFields["banco"]}
          />
        </Box>

        <Box>
          <DropdownField
            title="Tabela"
            dropdownProps={{
              w: "100%",
              children:
                currTabelaDesc?.length! > 66
                  ? currTabelaDesc?.slice(0, 66) + "..."
                  : undefined
            }}
            onChange={(value) => setDigitacao("tabela", { value })}
            value={tabela}
            options={tabelasOptions}
            errorMessage={invalidFields["tabela"]}
            isLoading={loadingTabelas}
          />
        </Box>

        <Box>
          <DropdownField
            title="Usuário Digitação"
            dropdownProps={{ w: "100%" }}
            onChange={(username) => {
              setDigitacao("usuarioDigitacao", { value: username });
              if (banco === "BMG") {
                setDigitacao("usuarioDigitacaoSenha", {
                  value: usuarioDataDigitacao.get(username)?.password || ""
                });
              }
              if (banco === "C6") {
                setDigitacao("usuarioDigitacaoSenha", {
                  value: usuarioDataDigitacao.get(username)?.password || ""
                });
                setDigitacao("cpfUsuarioCertificado", {
                  value:
                    usuarioDataDigitacao.get(username)?.cpfUsuarioCertificado ||
                    ""
                });
              }
            }}
            value={usuarioDigitacao}
            options={usuariosDigitacaoOptions}
            isLoading={loading}
            errorMessage={
              invalidFields["usuarioDigitacao"] ||
              invalidFields["cpfUsuarioCertificado"]
            }
          />
        </Box>
      </Grid>
      {banco === "C6" &&
        tabela &&
        selectedTipoColunaMargem?.productType &&
        selectedUser?.username &&
        selectedUser?.password && (
          <Box w="100%" mb="10px">
            <Text fontWeight="medium" mb="8px">
              Pacote de Benefício
            </Text>
            <RadioGroup
              value={`${enabledPacotesBeneficios}`}
              onChange={(value) => {
                const toBoolean = value === "true";
                setEnabledPacotesBeneficios(toBoolean);
                if (toBoolean) {
                  getPacotesBeneficios();
                }
              }}
            >
              <Radio value={"true"} mr="8px">
                <Text fontSize="14px">Sim</Text>
              </Radio>
              <Radio value="false">
                <Text fontSize="14px">Não</Text>
              </Radio>
            </RadioGroup>
            {enabledPacotesBeneficios && (
              <Box w="100%" my="10px">
                <CustomTableColor
                  columns={colunasPacotesBeneficios}
                  rows={pacotesBeneficios}
                  showControls={false}
                  loadingRowsCount={3}
                  isLoading={loadingPacotesBeneficios}
                />
              </Box>
            )}
          </Box>
        )}

      {tipoColunaMargem === "MARGEM_AUMENTO" && (
        <NovaIsencaoField
          onChange={(is) => setDigitacao("novaIsencao", { value: is })}
          value={novaIsencao}
        />
      )}
    </Box>
  );
}

const bancosDigitacaoMailingRefinCarteira = [
  { name: "C6", value: "C6" },
  { name: "Daycoval", value: "DAYCOVAL" }
];

export const bancosDigitacaoMailingPipeline = [
  { name: "Bmg", value: "BMG" },
  { name: "Facta", value: "FACTA" },
  { name: "C6", value: "C6" },
  { name: "iCred", value: "ICRED" },
  { name: "Presença", value: "PRESENCA" },
  { name: "Pan", value: "PAN" }
];

const tipoColunaMargemOptions = [
  { name: "Margem Aumento", value: "MARGEM_AUMENTO", productType: "0007" },
  { name: "Margem Livre", value: "MARGEM_LIVRE", productType: "0001" }
  // { name: "Margem IR Residual", value: "MARGEM_IR_RESIDUAL", productType: "0001" },
];
